import { useEffect, useRef, useState } from 'react'

import type { ReportRequest } from '~/widgets/CostBomDemo/components/HeroComposer'

import { TagManagerEventType } from '~/platform/utils/googleTagManager/tagManagerModels'
import { triggerTagManagerEvent } from '~/platform/utils/googleTagManager/tagManagerUtils'
import citricAcidImg from '~/widgets/CostBomDemo/assets/images/citric-acid.png'
import nmcBatteryImg from '~/widgets/CostBomDemo/assets/images/nmc-battery.png'
import smartHomeSocImg from '~/widgets/CostBomDemo/assets/images/smart-home-soc.png'
import { HeroComposer } from '~/widgets/CostBomDemo/components/HeroComposer'
import { HeroReportModal } from '~/widgets/CostBomDemo/components/HeroReportModal'
import { HeroSampleTabs } from '~/widgets/CostBomDemo/components/HeroSampleTabs'
import { resetLegacyEngine } from '~/widgets/CostBomDemo/engine/runLegacyEngine'
import { useCostModelDemo } from '~/widgets/CostBomDemo/hooks/useCostModelDemo'
import { useCostModelLead } from '~/widgets/CostBomDemo/hooks/useCostModelLead'
import {
  COST_BOM_DEMO_STRINGS,
  REGISTER_URL,
  SAMPLE_PRODUCTS,
} from '~/widgets/CostBomDemo/strings'
import '~/widgets/CostBomDemo/styles/bomScoped.css'
import '~/widgets/CostBomDemo/styles/breakdown.css'
import '~/widgets/CostBomDemo/styles/cm2.css'
import '~/widgets/CostBomDemo/styles/costBomDemo.css'
import '~/widgets/CostBomDemo/styles/heroCard.css'
import '~/widgets/CostBomDemo/styles/heroComposer.css'
import '~/widgets/CostBomDemo/styles/heroFigma.css'
import '~/widgets/CostBomDemo/styles/heroReport.css'
import '~/widgets/CostBomDemo/styles/tabs.css'

const PRODUCT_IMAGES: Record<string, string> = {
  'NMC Battery Cell': nmcBatteryImg,
  'Citric Acid': citricAcidImg,
  'Smart Home SoC': smartHomeSocImg,
}

export function CostBomDemoInner() {
  const rootRef = useRef<HTMLElement>(null)
  const { submitLead } = useCostModelLead()
  useCostModelDemo({
    rootRef,
    productImages: PRODUCT_IMAGES,
    onLead: submitLead,
  })
  const [report, setReport] = useState<ReportRequest | null>(null)

  // The legacy engine invokes this global when a visitor clicks one of its
  // "build your own cost model" CTAs. It opens the trial signup in a new tab,
  // keeping the demo page open behind it.
  //
  // The GA4 event is pushed here rather than caught by a GTM click trigger:
  // every CTA that lands here calls stopPropagation() (see heroControls.ts and
  // cm2Engine.ts), so GTM's document-level click listener never sees the click.
  useEffect(() => {
    window.openHeroConversionModal = () => {
      triggerTagManagerEvent(TagManagerEventType.costModelRegisterClick)
      window.open(REGISTER_URL, '_blank', 'noopener')
    }
    return () => {
      delete window.openHeroConversionModal
    }
  }, [])

  useEffect(() => () => resetLegacyEngine(), [])

  return (
    <div className="costBomDemo">
      <section
        ref={rootRef}
        className="mi-hero-section solution cost_model hero-figma"
        id="hero"
        data-hero-phase="idle"
      >
        <div className="hero-frame">
          <div className="hero-shell">
            <div className="hero-card" id="cm2-inline-browser">
              <div className="hero-card__chat">
                <HeroComposer onSubmit={setReport} />
                <div
                  className="hero-gen-status"
                  id="hero-gen-status"
                  aria-live="polite"
                >
                  <span className="hero-gen-status-dot" aria-hidden="true" />
                  <span
                    className="hero-gen-status-txt"
                    id="hero-gen-status-txt"
                  >
                    Understanding your product…
                  </span>
                </div>
                {/* The engine swaps this image per product. The chat layout has
                    no room for it, so it stays in the DOM but out of view. */}
                <div className="hero-product-clip" aria-hidden="true">
                  <img
                    className="hero-product-visual is-shown"
                    id="hero-product-visual"
                    src={nmcBatteryImg}
                    alt=""
                    aria-hidden="true"
                    data-product="NMC Battery Cell"
                  />
                </div>
              </div>

              <div className="hero-card__example">
                <p className="hero-card__example-label">
                  {COST_BOM_DEMO_STRINGS.exampleLabel}
                </p>
                <div className="hero-card__result">
                  <HeroSampleTabs heroRef={rootRef} />
                  <div className="hero-card__panels cm2-panels" id="cm2-panels">
                    <div id="hero-sample-root" />
                  </div>
                </div>
              </div>

              <div
                className="mi-bd"
                id="mi-bd"
                role="dialog"
                aria-label="Cost breakdown"
              />

              <div
                className="mi-window__tabs cm2-tabs"
                id="cm2-tabs"
                role="tablist"
                hidden
              >
                <div className="cm2-tabs__strip" id="cm2-tabs-strip">
                  <div
                    className="hero-tab-add-wrap hero-dd"
                    id="hero-tab-add-wrap"
                    hidden
                  >
                    <button
                      type="button"
                      className="cm2-tab-add"
                      id="hero-tab-add"
                      aria-haspopup="listbox"
                      aria-expanded="false"
                      aria-label="Add sample"
                    >
                      <svg viewBox="0 0 13 13" fill="none" aria-hidden="true">
                        <path
                          d="M6.5 1v11M1 6.5h11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                    <div
                      className="hero-dd-pop hero-tab-add-pop"
                      id="hero-tab-add-pop"
                      hidden
                      role="listbox"
                    >
                      <div className="hero-tab-add-pop-hd">
                        {COST_BOM_DEMO_STRINGS.addSample}
                      </div>
                      {SAMPLE_PRODUCTS.map((sample, idx) => (
                        <div key={sample.key}>
                          {idx > 0 && (
                            <div
                              className="hero-dd-divider"
                              role="separator"
                              aria-hidden="true"
                            />
                          )}
                          <button
                            type="button"
                            className="hero-dd-opt"
                            data-product={sample.product}
                            data-country={sample.country}
                            data-key={sample.key}
                          >
                            <span className="hero-dd-opt-name">
                              {sample.product}
                            </span>
                            <span className="hero-dd-opt-mid">
                              {COST_BOM_DEMO_STRINGS.sourcingFrom}
                            </span>
                            <span className="hero-dd-opt-country">
                              {sample.country}
                            </span>
                          </button>
                        </div>
                      ))}
                      <div
                        className="hero-dd-divider"
                        role="separator"
                        aria-hidden="true"
                      />
                      <button
                        type="button"
                        className="hero-dd-opt hero-dd-opt--custom"
                        data-custom="true"
                      >
                        <span className="hero-dd-opt-custom">
                          <span className="hero-dd-opt-name">
                            {COST_BOM_DEMO_STRINGS.yourOwnProduct}
                          </span>
                          <span className="hero-dd-opt-hint">
                            {COST_BOM_DEMO_STRINGS.describeProduct}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  className="hero-tab-cta-wrap"
                  id="hero-tab-cta-wrap"
                  hidden
                >
                  <button
                    type="button"
                    className="hero-tab-cta"
                    id="build-cost-model"
                  >
                    <span>{COST_BOM_DEMO_STRINGS.buildYourOwn}</span>
                    {/* Diagonal, not straight: this opens signup in a new tab. */}
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M7 17L17 7M17 7H9M17 7V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HeroReportModal request={report} onClose={() => setReport(null)} />
    </div>
  )
}
